/**
 * Why a person was paid in a month they billed no claims — the "Why" column on
 * Payroll without claims (review #230 follow-up, 2026-10-09).
 *
 * One reason per person-month, the first rule that applies, in `REASONS` order: the
 * month's own events (left, on leave, just hired) before the person's standing role
 * (manages people, a support title), before "worked claims but billed none". A
 * person's own reason is the one carrying the most of their dollars.
 *
 * These are explanations, not measures: nothing here changes a dollar. Every dollar on
 * the page gets exactly one reason, so the reason totals add up to the page total.
 *
 * Which are read straight off a source column and which lean on a job-title keyword is
 * in each reason's `basis`, and on Data & method.
 */
import type { Person } from '@/data/selectors'
import type { NoClaimPerson, PayrollWithoutClaims } from './payrollWithoutClaims'

export type ReasonKey = 'left' | 'leave' | 'newHire' | 'manager' | 'support' | 'working' | 'unknown'

export interface Reason {
  key: ReasonKey
  /** The column's text. */
  label: string
  /** Plural, for the totals strip: "Supervisors $2.1M". */
  plural: string
  /** The tooltip and Data & method's sentence. */
  explain: string
  /** Read straight off a source column, or a job-title guess. */
  basis: 'data' | 'heuristic' | 'mixed'
  /** Where it comes from, for Data & method. */
  source: string
}

/** A new hire's claimless months: the hire month and the two after it. */
export const NEW_HIRE_MONTHS = 3
/** A month is leave when PTO is at least this share of its paid hours. */
export const LEAVE_PTO_SHARE = 0.5

/** In precedence order: the first that applies wins. */
export const REASONS: readonly Reason[] = [
  {
    key: 'left',
    label: 'Left / final pay',
    plural: 'Left / final pay',
    explain:
      'Paid in or after the month of their termination date — a last paycheck or a payout, not a month of work.',
    basis: 'data',
    source: 'DimEmployee.TerminationDate',
  },
  {
    key: 'leave',
    label: 'On leave / PTO',
    plural: 'On leave / PTO',
    explain: `At least ${LEAVE_PTO_SHARE * 100}% of the month’s paid hours were PTO. Only Paylocity records PTO; offshore and other payrolls carry none, so their leave shows under another reason.`,
    basis: 'data',
    source: 'FactPayroll.PTOHours ÷ FactPayroll.LaborHours, by month',
  },
  {
    key: 'newHire',
    label: 'New hire',
    plural: 'New hires',
    explain: `Within ${NEW_HIRE_MONTHS} months of their hire date (the hire month and the ${NEW_HIRE_MONTHS - 1} after it) — training before a worklist.`,
    basis: 'data',
    source: 'DimEmployee.HireDate, the earliest across the person’s records',
  },
  {
    key: 'manager',
    label: 'Supervisor / manager',
    plural: 'Supervisors',
    explain:
      'Flagged as management, has people reporting to them, or holds a supervisor, manager, director, lead, VP or president title. Their pay is overhead on their team’s claims.',
    basis: 'mixed',
    source: 'DimEmployee.ManagementFlag, SupervisorFCCKey; job-title keywords',
  },
  {
    key: 'support',
    label: 'Trainer / QA / admin',
    plural: 'Trainers, QA, admin',
    explain:
      'A job title that supports the billers rather than bills: trainer, audit or QA, assistant, administrative, support, coordinator, analyst, receptionist, data entry or implementation — and authorization or referral work (MLTSS Authorization Department Representative, HMO Referral Specialist), which never has claims assigned directly — and RFMS Specialist.',
    basis: 'heuristic',
    source: 'DimEmployee.JobTitle keywords',
  },
  {
    key: 'working',
    label: 'Worked claims, none billed',
    plural: 'Worked, none billed',
    explain:
      'Touched claims that month, but no billed claim was attributed to them — follow-up and collections on claims billed by someone else, or in another month.',
    basis: 'data',
    source: 'FactClaimTouches (claims touched), FactOpenClaims (own pile worked), by month',
  },
  {
    key: 'unknown',
    label: 'Unknown',
    plural: 'Unknown',
    explain:
      'None of the above: no termination, no PTO recorded, not newly hired, no management or support title, and no claim touched that month.',
    basis: 'data',
    source: 'the rules above, none of them applying',
  },
]

export const REASON: Record<ReasonKey, Reason> = Object.fromEntries(REASONS.map((r) => [r.key, r])) as Record<
  ReasonKey,
  Reason
>

const MANAGER_TITLE = /\b(supervisor|sub-supervisor|director|vp|svp|president|head|lead)\b/i
// "Account Manager" and "Clinical Case Manager" manage accounts and cases, not people.
const MANAGER_WORD = /(?<!\baccount |\bcase )\bmanager\b/i
const SUPPORT_TITLE =
  /\b(trainer|training|audit|auditor|qa|quality|assistant|administrative|admin|support|coordinator|analyst|receptionist|data entry|implementation|auth|authorization|referrals?|rfms)\b/i

export const isManagerTitle = (title: string) => MANAGER_TITLE.test(title) || MANAGER_WORD.test(title)
export const isSupportTitle = (title: string) => SUPPORT_TITLE.test(title)

/** 'YYYY-MM' → months since year 0, so two months subtract. */
const monthIndex = (m: string) => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1

/** The month's figures the rules read. All optional: an older bundle lacks some. */
export interface MonthFacts {
  month: string
  laborHours?: number | null
  ptoHours?: number | null
  claimsTouched?: number | null
  openClaimsWorked?: number | null
}

export type PersonFacts = Pick<Person, 'jobTitle' | 'management' | 'terminationDate' | 'hireDate'>

/** The reason for one claimless month. `hasReports`: someone names them as supervisor. */
export function monthReason(p: PersonFacts, m: MonthFacts, hasReports: boolean): ReasonKey {
  if (p.terminationDate !== null && m.month >= p.terminationDate.slice(0, 7)) return 'left'
  const hours = m.laborHours ?? 0
  if (hours > 0 && (m.ptoHours ?? 0) / hours >= LEAVE_PTO_SHARE) return 'leave'
  if (p.hireDate) {
    const since = monthIndex(m.month) - monthIndex(p.hireDate.slice(0, 7))
    if (since >= 0 && since < NEW_HIRE_MONTHS) return 'newHire'
  }
  if (p.management || hasReports || isManagerTitle(p.jobTitle)) return 'manager'
  if (isSupportTitle(p.jobTitle)) return 'support'
  if ((m.claimsTouched ?? 0) > 0 || (m.openClaimsWorked ?? 0) > 0) return 'working'
  return 'unknown'
}

export interface PersonWhy {
  /** Claimless month → its reason. */
  byMonth: Map<string, ReasonKey>
  /** The reason carrying most of their dollars; ties go to the earlier in `REASONS`. */
  reason: ReasonKey
}

const ORDER = new Map(REASONS.map((r, i) => [r.key, i]))

/**
 * Each row's reasons. `reporting`: every name someone in the data reports to directly —
 * read off the whole population, not the page's rows, so a supervisor still counts
 * when their reports billed every month.
 */
export function explainRows(rows: readonly NoClaimPerson[], reporting: ReadonlySet<string>): Map<string, PersonWhy> {
  const out = new Map<string, PersonWhy>()
  for (const r of rows) {
    const facts = new Map(r.person.months.map((m) => [m.month, m]))
    const byMonth = new Map<string, ReasonKey>()
    const dollars = new Map<ReasonKey, number>()
    for (const [month, v] of r.withoutClaims) {
      const key = monthReason(r.person, facts.get(month) ?? { month }, reporting.has(r.person.employee))
      byMonth.set(month, key)
      dollars.set(key, (dollars.get(key) ?? 0) + v)
    }
    let reason: ReasonKey = 'unknown'
    let best = -Infinity
    for (const [key, v] of dollars) {
      if (v > best || (v === best && ORDER.get(key)! < ORDER.get(reason)!)) {
        best = v
        reason = key
      }
    }
    out.set(r.person.employee, { byMonth, reason })
  }
  return out
}

/**
 * The reason a row shows. With no filter, the person's own; under a reason filter, that
 * reason — every month left on the row is that reason's, so the person's overall reason
 * (often another) would contradict the chip (review #393). The months with another
 * reason are then the dashed ones, and `byMonth` still names them.
 */
export function shownWhy(why: PersonWhy, filter: ReasonKey | 'all'): PersonWhy {
  return filter === 'all' ? why : { byMonth: why.byMonth, reason: filter }
}

/** Dollars per reason, month by month, so they add up to the page total. */
export function reasonTotals(rows: readonly NoClaimPerson[], why: ReadonlyMap<string, PersonWhy>) {
  const out = new Map<ReasonKey, { total: number; people: Set<string> }>()
  for (const r of rows) {
    const w = why.get(r.person.employee)
    for (const [month, v] of r.withoutClaims) {
      const key = w?.byMonth.get(month) ?? 'unknown'
      const t = out.get(key) ?? { total: 0, people: new Set<string>() }
      t.total += v
      t.people.add(r.person.employee)
      out.set(key, t)
    }
  }
  return out
}

/**
 * The page cut to one reason, at month grain: each row keeps only that reason's months,
 * and a row with none goes. Totals are recomputed, so they tie to the reason's chip.
 */
export function onlyReason(
  result: PayrollWithoutClaims,
  why: ReadonlyMap<string, PersonWhy>,
  reason: ReasonKey,
): PayrollWithoutClaims {
  const rows: NoClaimPerson[] = []
  const byMonth = new Map<string, number>()
  let total = 0
  for (const r of result.rows) {
    const w = why.get(r.person.employee)
    const withoutClaims = new Map([...r.withoutClaims].filter(([m]) => (w?.byMonth.get(m) ?? 'unknown') === reason))
    if (withoutClaims.size === 0) continue
    let own = 0
    for (const [m, v] of withoutClaims) {
      own += v
      byMonth.set(m, (byMonth.get(m) ?? 0) + v)
    }
    total += own
    rows.push({ ...r, withoutClaims, total: own })
  }
  rows.sort((a, b) => b.total - a.total || a.person.employee.localeCompare(b.person.employee))
  return { rows, byMonth, total }
}
